import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RefreshIcon from '@mui/icons-material/Refresh';
import { NetworkStatus, useQuery } from '@apollo/client';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatISO, startOfDay, endOfDay } from 'date-fns';
import { FC, useState } from 'react';
import {
  ArticleFormat,
  ArticleOrderingInput,
  ArticleStatus,
  BrandKey,
} from '../../../__generated__/queries-web';
import { formatDate } from '../../../utils/dateUtils';
import {
  GET_ARTICLE_PLANNING,
  GetArticlePlanningQuery,
  GetArticlePlanningQueryVariables,
} from './getArticlePlanning.web.graphql';

interface PlanningSectionProps {
  brandKey: BrandKey | undefined;
  currentArticleId: string | undefined;
}

export const PlanningSection: FC<PlanningSectionProps> = ({
  brandKey,
  currentArticleId,
}) => {
  const today = formatDate(new Date(), 'yyyy-MM-dd') ?? '';
  const [selectedDate, setSelectedDate] = useState(today);

  const selectedDay = selectedDate ? new Date(selectedDate) : new Date();

  const { data, loading, refetch, networkStatus } = useQuery<
    GetArticlePlanningQuery,
    GetArticlePlanningQueryVariables
  >(GET_ARTICLE_PLANNING, {
    notifyOnNetworkStatusChange: true,
    skip: !brandKey || !selectedDate,
    variables: {
      where: {
        brandKey,
        format: ArticleFormat.Rich,
        publishedAt_gte: formatISO(startOfDay(selectedDay)),
        publishedAt_lt: formatISO(endOfDay(selectedDay)),
        status_in: [
          ArticleStatus.Published,
          ArticleStatus.Scheduled,
          ArticleStatus.ScheduledForRepublication,
        ],
      },
      orderBy: [ArticleOrderingInput.publishedAt_ASC],
    },
    fetchPolicy: 'cache-and-network',
  });

  const isRefetching = networkStatus === NetworkStatus.refetch;
  const articles = data?.articles ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Divider />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarMonthIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Typography
            variant="overline"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              letterSpacing: '0.1em',
              lineHeight: 1,
            }}
          >
            Planning
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            type="date"
            size="small"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            sx={{
              '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.6, px: 1 },
              '& .MuiOutlinedInput-root': { borderRadius: 1 },
            }}
          />
          <Tooltip title="Rafraîchir">
            <IconButton
              size="small"
              onClick={() => refetch()}
              disabled={loading}
              sx={{
                color: 'text.secondary',
                '& svg': {
                  fontSize: 16,
                  transition: 'transform 0.6s ease',
                  ...(isRefetching && {
                    animation: 'spin 0.7s linear infinite',
                  }),
                },
                '@keyframes spin': {
                  from: { transform: 'rotate(0deg)' },
                  to: { transform: 'rotate(360deg)' },
                },
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={18} />
        </Box>
      )}

      {!loading && articles.length === 0 && selectedDate && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Aucun article publié ou programmé ce jour.
        </Typography>
      )}

      {articles.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {articles.map((article) => {
            const isCurrent = article.id === currentArticleId;
            const isPublished = article.status === ArticleStatus.Published;
            const opacity = !isCurrent && isPublished ? 0.45 : 1;
            const time = article.publishedAt
              ? formatDate(new Date(article.publishedAt), 'HH:mm')
              : '—';

            return (
              <Box
                key={article.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  opacity,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontVariantNumeric: 'tabular-nums',
                    fontWeight: isCurrent ? 700 : 400,
                    minWidth: 34,
                    color: isCurrent ? 'primary.main' : 'text.secondary',
                    flexShrink: 0,
                  }}
                >
                  {time}
                </Typography>

                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: isCurrent ? 'error.main' : 'divider',
                  }}
                />

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: isCurrent ? 'primary.main' : 'divider',
                    backgroundColor: isCurrent
                      ? 'primary.main'
                      : 'background.paper',
                    color: isCurrent ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isCurrent ? 600 : 400,
                      fontSize: '0.8125rem',
                      lineHeight: 1.4,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {article.title}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};
